import { clsx } from "clsx";
import { JSX } from "react";
import { spinnerOverlay } from "../button/Button.css";
import type { IconProps } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import { ProgressSpinner } from "../progressSpinner/ProgressSpinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip/Tooltip";
import {
  disabledTooltipAnchor,
  iconButtonBase,
  iconButtonSizes,
  iconButtonVariants,
  iconHidden,
  iconWrapper
} from "./IconButton.css";

interface IconButtonPropsCustom {
  /**
   * The icon to be displayed in the button.
   * This should be a valid icon name that the Icon component can render.
   */
  iconName: IconProps["name"];
  /**
   * The size of the button.
   * This affects the button's font size and height.
   * Options include: "md", "sm", "lg".
   */
  size?: keyof typeof iconButtonSizes;
  /**
   * The variant of the button.
   * This determines the button's appearance and behavior.
   * Options include: "primary", "secondary", "tertiary".
   */
  variant?: keyof typeof iconButtonVariants;
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether the button is in a loading state.
   */
  isLoading?: boolean;
  /**
   * Names the button on hover and focus, and doubles as its `aria-label`
   * unless one is passed. An icon-only control needs a name either way.
   * A `disabled` button keeps its tooltip on hover — the place to say why
   * it is disabled. It takes no focus, so there the `aria-label` names it.
   */
  tooltip?: string;

  /**
   * Additional class name(s) to apply to the button for custom styling.
   */
  className?: string;
}

export interface IconButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    IconButtonPropsCustom {}

export function IconButton({
  variant = "primary",
  size = "md",
  iconName,
  disabled,
  isLoading,
  tooltip,
  className,
  ...props
}: IconButtonProps): JSX.Element {
  const button = (
    <button
      className={clsx(
        iconButtonBase,
        iconButtonVariants[variant],
        iconButtonSizes[size],
        className
      )}
      disabled={disabled}
      aria-label={tooltip}
      {...props}
      aria-busy={isLoading}
    >
      <span
        className={clsx(iconWrapper, isLoading && iconHidden)}
        aria-hidden="true"
      >
        <Icon name={iconName} />
      </span>
      {isLoading && (
        <span className={spinnerOverlay}>
          <ProgressSpinner />
        </span>
      )}
    </button>
  );

  if (tooltip === undefined) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? (
          <span className={disabledTooltipAnchor}>{button}</span>
        ) : (
          button
        )}
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
